// Ported from genieos/web/src/components/gamma/Modal.tsx @ 84923d036
import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { cn } from '@/utils/cn';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import type { ModalProps, ModalSize } from './types';

// Spec §27.1 — four-step sizing scale. Every <Modal> instance must pick
// exactly one. `xl` is retained as a deprecated alias mapping to `lg` for
// backward compat; new code should use the spec sizes.
const sizeStyles: Record<ModalSize, string> = {
  sm:   'max-w-md min-w-[320px]',
  md:   'max-w-2xl min-w-[480px]',
  lg:   'max-w-4xl min-w-[640px] lg:min-w-[800px]',
  xl:   'max-w-4xl min-w-[640px] lg:min-w-[800px]', // deprecated — maps to lg
  full: 'max-w-[90vw] lg:max-w-[85vw] xl:max-w-[3200px] min-w-[320px] sm:min-w-[640px] lg:min-w-[1024px]',
};

const backdropAnimation = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.2 } },
  exit: { opacity: 0, transition: { duration: 0.2 } },
};

const modalAnimation = {
  initial: { opacity: 0, scale: 0.9, y: 20 },
  animate: {
    opacity: 1, scale: 1, y: 0,
    transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
  },
  exit: {
    opacity: 0, scale: 0.9, y: 20,
    transition: { duration: 0.2 },
  },
};

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  closable = true,
  className,
  chromeless = false,
  // Legacy props — accepted but ignored
  critical: _critical,
  emergencyModal: _emergencyModal,
  confirmationRequired: _confirmationRequired,
}) => {
  // Track whether the modal should be in the DOM.  AnimatePresence exit
  // animations can stall when heavy children (e.g. SiteCommandsPage) trigger
  // re-renders during the exit, leaving the fixed backdrop in the DOM and
  // blocking all clicks on the page beneath.  We keep a separate "rendered"
  // flag and force-remove the DOM after a short timeout if the exit animation
  // doesn't complete.
  const [rendered, setRendered] = useState(isOpen);
  const focusTrapRef = useFocusTrap(isOpen);

  useEffect(() => {
    if (isOpen) {
      setRendered(true);
    } else if (rendered) {
      // Give AnimatePresence 400ms to finish exit animations (200ms + margin),
      // then force-remove the overlay regardless.
      const timer = setTimeout(() => setRendered(false), 400);
      return () => clearTimeout(timer);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && closable) onClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose, closable]);

  if (!rendered) return null;

  return (
    <AnimatePresence onExitComplete={() => setRendered(false)}>
      {isOpen && (
        <div className={cn('fixed inset-0 flex items-center justify-center p-4 z-50')}>
          {/* Backdrop — solid dark, no blur */}
          <motion.div
            className="absolute inset-0 bg-black/60"
            variants={backdropAnimation}
            initial="initial"
            animate="animate"
            exit="exit"
            onClick={closable ? onClose : undefined}
            aria-hidden="true"
          />

          {/* Modal */}
          <motion.div
            ref={focusTrapRef}
            className={cn(
              'relative w-full max-h-[90vh] overflow-hidden',
              !chromeless && sizeStyles[size],
              className
            )}
            variants={modalAnimation}
            initial="initial"
            animate="animate"
            exit="exit"
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-title"
          >
            {chromeless ? (
              children
            ) : (
              <div className="bg-ems-bg-card rounded-xl border border-ems-border overflow-hidden flex flex-col max-h-[90vh]">
                {/* Header */}
                {/* §27.3 Standard header — px-6 py-4, not p-6 */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-ems-border">
                  <div id="modal-title" className="text-lg font-semibold text-ems-text-primary">
                    {title}
                  </div>
                  {closable && (
                    <button
                      onClick={onClose}
                      className="p-2 rounded-lg text-ems-text-muted hover:text-ems-text-primary hover:bg-ems-bg-card-hover transition-colors"
                      aria-label="Close modal"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                  {children}
                </div>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
